import { Box, Avatar, ChevronRightIcon, LoadingIndicator } from '@community_dev/pixels'
import type { JSX } from 'react'
import styled, { css, useTheme } from 'styled-components'

import { CommunityClient } from 'types/communityClient'
import { formatPhone } from 'utils/normalize'

type StyledButtonProps = {
  $active?: boolean
}

const StyledButton = styled.button<StyledButtonProps>`
  width: 100%;
  border: none;
  background: none;
  padding: 16px 0;
  text-align: left;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme?.COLORS?.BORDERS};
  cursor: pointer;
  transition: opacity 0.2s ease-in-out;

  ${({ $active }) =>
    !$active &&
    css`
      opacity: 0.5;

      &:hover {
        opacity: 1;
      }
    `}
`

const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 -40px;

  li {
    padding: 0 40px;

    &:hover {
      background: ${({ theme }) => theme?.COLORS?.DEPRECATED_HOVER};
    }

    &:last-child ${StyledButton} {
      border: none;
    }
  }

  ${Avatar} {
    margin-right: 8px;
  }

  p {
    margin: 0;
  }
`

const StyledDetails = styled.div`
  flex-grow: 1;
`

const StyledName = styled.p`
  ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.BODY1};
  font-weight: 600;
`

const StyledSubHeading = styled.div`
  ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.BODY1};
  margin-top: 16px;
  margin-bottom: 8px;
  color: ${({ theme }) => theme?.COLORS?.SUBTEXT};
`

const StyledPhone = styled.p`
  color: ${({ theme }) => theme?.COLORS?.SUBTEXT};
  ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.CAPTION1};
  font-weight: 500;

  span {
    ${({ theme }) => theme?.TYPOGRAPHY?.VARIANT?.CAPTION1};
    font-weight: 600;
    color: ${({ theme }) => theme?.COLORS?.LINKS};
  }
`

export function ClientSelection({
  clients,
  onSelect,
  subHeading,
}: {
  clients: CommunityClient[]
  onSelect: (client: CommunityClient) => unknown
  subHeading?: string
}): JSX.Element {
  const { COLORS } = useTheme() || {}
  return clients.length > 0 ? (
    <Box>
      <Box.Content>
        <h1>Select Account</h1>
        {subHeading !== undefined && <StyledSubHeading>{subHeading}</StyledSubHeading>}
        <StyledList>
          {clients.map((client) => (
            <li key={client.clientId}>
              <StyledButton
                onClick={() => {
                  onSelect(client)
                }}
                type="button"
              >
                <Avatar name={`${client.firstName} ${client.lastName || ''}`} src={client.clientImage} />
                <StyledDetails>
                  <StyledName>{`${client.firstName} ${client.lastName || ''}`}</StyledName>
                  <StyledPhone>{formatPhone(client.phoneNumber)}</StyledPhone>
                </StyledDetails>
                <ChevronRightIcon color={COLORS?.SUBTEXT} />
              </StyledButton>
            </li>
          ))}
        </StyledList>
      </Box.Content>
    </Box>
  ) : (
    <LoadingIndicator />
  )
}
